import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";

import { searchFreshdeskCompanySuggestions } from "@/lib/masterData";
import { useTouchListPickSession } from "@/lib/useTouchListPickSession";
import { cn } from "@/lib/utils";
import { computeAnchoredListPanel, FLOATING_POPOVER_Z_INDEX } from "@/lib/viewportAnchoredPopover";

const DEBOUNCE_MS = 180;
const MIN_QUERY_LENGTH = 2;
const LIST_MAX_HEIGHT_PX = 224;

export type FreshdeskCompanySelection = {
  id: number;
  name: string;
};

function useDebouncedValue(value: string, delayMs: number, enabled: boolean): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs, enabled]);
  return enabled ? debounced : "";
}

function optionDomId(listboxId: string, index: number): string {
  return `${listboxId}-option-${index}`;
}

export function FreshdeskCompanyAutocomplete({
  id,
  company,
  onCompanyChange,
  placeholder = "Search Freshdesk companies…",
  disabled = false,
  className,
}: {
  id: string;
  company: FreshdeskCompanySelection | null;
  onCompanyChange: (company: FreshdeskCompanySelection | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}): JSX.Element {
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const touchListPick = useTouchListPickSession(open);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const [panelBox, setPanelBox] = useState<ReturnType<typeof computeAnchoredListPanel> | null>(null);

  const trimmedQuery = draft.trim();
  const queryEnabled = open && company == null && trimmedQuery.length >= MIN_QUERY_LENGTH;
  const debouncedQuery = useDebouncedValue(trimmedQuery, DEBOUNCE_MS, queryEnabled);

  const suggestionsQuery = useQuery({
    queryKey: ["freshdesk-company-suggestions", debouncedQuery],
    queryFn: () => searchFreshdeskCompanySuggestions(debouncedQuery),
    enabled: queryEnabled && debouncedQuery.length >= MIN_QUERY_LENGTH,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const suggestions = suggestionsQuery.data ?? [];
  const showPanel = open && company == null && trimmedQuery.length >= MIN_QUERY_LENGTH;

  function pickSuggestion(selection: FreshdeskCompanySelection): void {
    onCompanyChange(selection);
    setDraft("");
    setOpen(false);
    setHighlightedIndex(null);
  }

  const repositionPanel = () => {
    const anchor = rootRef.current;
    if (!anchor) {
      setPanelBox(null);
      return;
    }
    setPanelBox(
      computeAnchoredListPanel({
        anchorRect: anchor.getBoundingClientRect(),
        listMaxHeightCap: LIST_MAX_HEIGHT_PX,
        minWidth: anchor.offsetWidth,
      }),
    );
  };

  useLayoutEffect(() => {
    if (!showPanel) {
      setPanelBox(null);
      return;
    }
    repositionPanel();
    window.addEventListener("resize", repositionPanel);
    window.addEventListener("scroll", repositionPanel, true);
    return () => {
      window.removeEventListener("resize", repositionPanel);
      window.removeEventListener("scroll", repositionPanel, true);
    };
  }, [showPanel, suggestions.length, suggestionsQuery.isFetching]);

  useEffect(() => {
    if (!showPanel) {
      setHighlightedIndex(null);
      return;
    }
    if (suggestions.length === 0) {
      setHighlightedIndex(null);
      return;
    }
    setHighlightedIndex((current) => (current == null || current >= suggestions.length ? 0 : current));
  }, [showPanel, suggestions]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (rootRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Escape") {
      event.stopPropagation();
      setOpen(false);
      return;
    }
    if (showPanel && suggestions.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedIndex((current) => {
          if (current == null) {
            return 0;
          }
          return Math.min(current + 1, suggestions.length - 1);
        });
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndex((current) => {
          if (current == null) {
            return suggestions.length - 1;
          }
          return Math.max(current - 1, 0);
        });
        return;
      }
      if (event.key === "Enter" && highlightedIndex != null) {
        event.preventDefault();
        const suggestion = suggestions[highlightedIndex];
        if (suggestion) {
          pickSuggestion(suggestion);
        }
      }
    }
  }

  const panel = showPanel && panelBox
    ? createPortal(
        <div
          id={listboxId}
          role="listbox"
          className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg"
          style={{
            position: "fixed",
            top: panelBox.top,
            left: panelBox.left,
            width: panelBox.width,
            zIndex: FLOATING_POPOVER_Z_INDEX,
          }}
        >
          <div className="overflow-y-auto" style={{ maxHeight: panelBox.listContentMaxHeight }}>
            {suggestionsQuery.isFetching && suggestions.length === 0 ? (
              <div className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Searching companies…
              </div>
            ) : null}
            {!suggestionsQuery.isFetching && suggestions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-slate-500">No matching Freshdesk companies.</div>
            ) : null}
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.id}
                id={optionDomId(listboxId, index)}
                type="button"
                role="option"
                aria-selected={highlightedIndex === index}
                className={cn(
                  "flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-slate-50",
                  highlightedIndex === index && "bg-slate-50",
                )}
                onPointerDown={(event) => {
                  touchListPick.onListItemPointerDown(event, () => {
                    pickSuggestion(suggestion);
                  });
                }}
              >
                <span className="font-medium text-slate-900">{suggestion.name}</span>
                <span className="text-xs text-slate-500">Freshdesk company {String(suggestion.id)}</span>
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      {company != null ? (
        <div
          className={cn(
            "flex min-h-10 items-center gap-2 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm",
            disabled && "cursor-not-allowed bg-slate-100",
          )}
        >
          <span className="inline-flex min-w-0 flex-1 items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-800">
            <span className="truncate" title={company.name}>
              {company.name}
            </span>
            <button
              type="button"
              className="rounded p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800 disabled:pointer-events-none"
              aria-label={`Remove ${company.name}`}
              disabled={disabled}
              onClick={() => onCompanyChange(null)}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        </div>
      ) : (
        <input
          ref={inputRef}
          id={id}
          type="text"
          autoComplete="off"
          data-bwignore
          data-lpignore="true"
          disabled={disabled}
          value={draft}
          placeholder={placeholder}
          className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:cursor-not-allowed disabled:bg-slate-100"
          role="combobox"
          aria-expanded={showPanel}
          aria-controls={showPanel ? listboxId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={
            showPanel && highlightedIndex != null ? optionDomId(listboxId, highlightedIndex) : undefined
          }
          onChange={(event) => {
            setDraft(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            window.setTimeout(() => {
              if (touchListPick.shouldYieldBlurCommit()) return;
              setDraft("");
            }, 0);
          }}
        />
      )}
      {panel}
    </div>
  );
}
